import { differenceInMinutes, parse } from "date-fns";
import { Controller } from "stimulus";

export default class extends Controller {
  static values = {
    minimumEngagementMinutes: Number,
    mustHaveBreakMinutes: Number,
    minimumBreakMinutes: Number,
    hasPaidBreakRule: Number,
    paidBreakThresholdHours: Number,
    paidBreakMinutesOverThreshold: Number,
    paidBreakMinutesAtOrUnderThreshold: Number,
  };

  static targets = [
    "startAt",
    "finishAt",
    "totalTimeWorked",
    "breakStartTime",
    "breakFinishTime",
    "minimumEngagementGroup",
    "warningBreak",
    "positionSelect",
  ];
  declare startAtTarget: HTMLInputElement;
  declare finishAtTarget: HTMLInputElement;
  declare totalTimeWorkedTarget: HTMLElement;
  declare breakStartTimeTargets: Array<HTMLInputElement>;
  declare breakFinishTimeTargets: Array<HTMLInputElement>;
  declare hasBreakStartTimeTarget: boolean;
  declare hasBreakFinishTimeTarget: boolean;
  declare minimumEngagementGroupTarget: HTMLElement;
  declare warningBreakTarget: HTMLElement;
  declare positionSelectTarget: HTMLSelectElement;
  declare hasPositionSelectTarget: boolean;
  declare minimumEngagementMinutesValue: number;
  declare mustHaveBreakMinutesValue: number;
  declare minimumBreakMinutesValue: number;
  declare hasPaidBreakRuleValue: number;
  declare paidBreakThresholdHoursValue: number;
  declare paidBreakMinutesOverThresholdValue: number;
  declare paidBreakMinutesAtOrUnderThresholdValue: number;

  connect(): void {
    this.waitForFlatpickrDateValues().then(() => {
      this.applySelectedPositionValues();
      this.processingForm();
    });
  }

  // The position <select> (when the Weployee has more than one position available for the
  // day) carries each option's minimum-engagement/paid-break rule as data attributes, since
  // those rules depend on which position's award applies. Switching position doesn't reload
  // the page, so we re-read them from the newly selected option here.
  positionChanged(): void {
    this.applySelectedPositionValues();
    this.processingForm();
  }

  applySelectedPositionValues(): void {
    if (!this.hasPositionSelectTarget) return;

    const option = this.positionSelectTarget.selectedOptions[0];
    if (!option || !option.value) return;

    this.minimumEngagementMinutesValue = Number(
      option.dataset.minimumEngagementMinutes ?? 0
    );
    this.hasPaidBreakRuleValue = Number(option.dataset.hasPaidBreakRule ?? 0);
    this.paidBreakThresholdHoursValue = Number(
      option.dataset.paidBreakThresholdHours ?? 0
    );
    this.paidBreakMinutesOverThresholdValue = Number(
      option.dataset.paidBreakMinutesOverThreshold ?? 0
    );
    this.paidBreakMinutesAtOrUnderThresholdValue = Number(
      option.dataset.paidBreakMinutesAtOrUnderThreshold ?? 0
    );
  }

  processingForm(): void {
    const hasBreaks =
      this.hasBreakFinishTimeTarget && this.hasBreakStartTimeTarget;

    const totalBreakMinutes = this.getTotalBreakMinutes(
      this.breakStartTimeTargets,
      this.breakFinishTimeTargets,
      hasBreaks
    );
    const totalAttendanceMinutes = this.getElapsedMinutes(
      this.startAtTarget.value,
      this.finishAtTarget.value
    );

    this.handleHideShowWarnBreak(totalAttendanceMinutes, totalBreakMinutes);

    const paidBreakMinutes = this.getPaidBreakMinutes(
      totalAttendanceMinutes,
      totalBreakMinutes
    );
    const totalMinutesWorked =
      totalAttendanceMinutes - totalBreakMinutes + paidBreakMinutes;
    this.displayTotalTimeWorkedMessage(
      totalMinutesWorked,
      totalBreakMinutes,
      paidBreakMinutes
    );

    if (
      this.minimumEngagementMinutesValue > 0 &&
      totalMinutesWorked < this.minimumEngagementMinutesValue
    ) {
      this.showMinimumEngagementGroup();
    } else {
      this.hideMinimumEngagementGroup();
    }
  }

  handleHideShowWarnBreak(
    totalAttendanceMinutes: number,
    totalBreakMinutes: number
  ): void {
    const mustHaveBreak =
      this.mustHaveBreakMinutesValue < totalAttendanceMinutes;
    const enoughBreakMinutes =
      totalBreakMinutes >= this.minimumBreakMinutesValue;

    if (mustHaveBreak && !enoughBreakMinutes) this.showWarningBreak();
    else this.hideWarningBreak();
  }

  displayTotalTimeWorkedMessage(
    totalMinutesWorked: number,
    totalBreakMinutes: number,
    paidBreakMinutes: number
  ): void {
    const displayMessage = this.getDisplayMessage(
      totalMinutesWorked,
      totalBreakMinutes,
      paidBreakMinutes
    );
    this.totalTimeWorkedTarget.innerText = displayMessage;
  }

  getDisplayMessage(
    totalMinutesWorked: number,
    totalBreakMinutes: number,
    paidBreakMinutes: number
  ): string {
    if (totalMinutesWorked <= 0) return "Total hours must be greater than 0";
    if (totalBreakMinutes == -1) return "Invalid break present";
    if (Number.isNaN(totalMinutesWorked) || Number.isNaN(totalBreakMinutes))
      return "Invalid break present";
    const hoursWorked = Math.floor(totalMinutesWorked / 60);
    const minutesWorked = Math.floor(totalMinutesWorked % 60);
    const baseMessage = `${hoursWorked} hrs ${minutesWorked} mins`;
    if (paidBreakMinutes <= 0) return baseMessage;
    return `${baseMessage} (includes ${paidBreakMinutes} min paid rest break)`;
  }

  // Clause 20.4-style flat entitlement only - deliberately does not attempt to model
  // OT-linked rules (e.g. clause 21.6), which need overtime/level context this form doesn't
  // have. The authoritative, complete breakdown is shown at the Payrate Estimate approval step.
  getPaidBreakMinutes(
    totalAttendanceMinutes: number,
    totalBreakMinutes: number
  ): number {
    if (!this.hasPaidBreakRuleValue || totalBreakMinutes <= 0) return 0;

    const totalAttendanceHours = totalAttendanceMinutes / 60;
    const entitlementMinutes =
      totalAttendanceHours > this.paidBreakThresholdHoursValue
        ? this.paidBreakMinutesOverThresholdValue
        : this.paidBreakMinutesAtOrUnderThresholdValue;

    return Math.min(entitlementMinutes, totalBreakMinutes);
  }

  getTotalBreakMinutes(
    startTimeElements: Array<HTMLInputElement>,
    finishTimeElements: Array<HTMLInputElement>,
    hasBreaks: boolean
  ): number {
    if (!hasBreaks) return 0;

    const minutesList = startTimeElements.map(
      this.getDifferenceInMinutes(finishTimeElements)
    );

    if (minutesList.some((minutes) => minutes <= 0)) return -1;

    return minutesList.reduce(
      (totalMinutes, minutes): number => totalMinutes + minutes
    );
  }

  getDifferenceInMinutes(finishTimeInputList: Array<HTMLInputElement>) {
    return (startTimeInput: HTMLInputElement, index: number): number =>
      this.getElapsedMinutes(
        startTimeInput.value,
        finishTimeInputList[index].value
      );
  }

  getElapsedMinutes(startTimeString: string, finishTimeString: string): number {
    const referenceDate = new Date();
    const startTime = parse(startTimeString, "HH:mm", referenceDate);
    const finishTime = parse(finishTimeString, "HH:mm", referenceDate);
    return differenceInMinutes(finishTime, startTime);
  }

  waitForFlatpickrDateValues(): Promise<void> {
    return Promise.resolve();
  }

  showMinimumEngagementGroup(): void {
    this.minimumEngagementGroupTarget.classList.remove("hidden");
  }

  hideMinimumEngagementGroup(): void {
    this.minimumEngagementGroupTarget.classList.add("hidden");
  }

  showWarningBreak(): void {
    this.warningBreakTarget.classList.remove("hidden");
  }

  hideWarningBreak(): void {
    this.warningBreakTarget.classList.add("hidden");
  }
}
